// Transporte HTTP del API de Tarja (spec técnica §7.1).
// - JSON snake_case, errores RFC 7807 con `code` del catálogo (Anexo B).
// - Token de acceso solo en memoria; refresh por cookie HttpOnly (POST /auth/refresh) con reintento automático ante 401.
// - X-Tenant-Id cuando el usuario opera en más de un tenant.
// - ETag / If-Match para concurrencia optimista; Idempotency-Key en POST que crean recursos.

import type { CodigoError } from '@tarja/contracts';

export interface ErrorCampo {
  campo?: string;
  path?: (string | number)[] | string;
  mensaje?: string;
  message?: string;
  codigo?: string;
  [k: string]: unknown;
}

export interface ProblemDetail {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  code?: string;
  errors?: unknown[];
  instance?: string;
  [k: string]: unknown;
}

export type CodigoCliente = CodigoError | 'RED' | 'PRECONDICION_FALLIDA' | 'VERSION_OBSOLETA' | 'PRESUPUESTO_EXCEDIDO' | 'ERROR_INTERNO' | 'SESION_EXPIRADA' | `HTTP_${number}`;

/** Mensajes en español para los códigos del catálogo; el `detail` del servidor se muestra además cuando existe. */
export const MENSAJES_ERROR: Record<string, string> = {
  AUTH_CREDENCIALES: 'Usuario o contraseña incorrectos.',
  AUTH_MFA_REQUERIDO: 'Falta el segundo factor de autenticación.',
  AUTH_MFA_INVALIDO: 'El código de verificación no es válido o expiró.',
  AUTH_TOKEN_INVALIDO: 'Tu sesión no es válida. Vuelve a ingresar.',
  AUTH_BLOQUEADO: 'Usuario bloqueado temporalmente por intentos fallidos. Intenta más tarde.',
  DISPOSITIVO_BLOQUEADO: 'El dispositivo está bloqueado.',
  FIRMA_DISPOSITIVO_INVALIDA: 'Firma del dispositivo inválida.',
  TENANT_NO_AUTORIZADO: 'No tienes acceso a esta organización.',
  PERMISO_DENEGADO: 'No tienes permiso para realizar esta acción.',
  SEGREGACION: 'Esta acción es incompatible con otra función que ya ejerciste (segregación de funciones).',
  NO_ENCONTRADO: 'El registro no existe o no tienes acceso.',
  VIGENCIA_TRASLAPADA: 'La vigencia se superpone con otra versión del mismo registro.',
  TARIFA_EN_USO: 'La tarifa ya se usó en un cálculo: crea una nueva versión o una excepción.',
  ID_REUTILIZADO: 'El identificador ya existe con contenido distinto.',
  PERIODO_BLOQUEADO: 'El periodo tiene controles bloqueantes pendientes.',
  PERIODO_CERRADO: 'La operación no está permitida en un periodo cerrado.',
  TRANSICION_INVALIDA: 'La acción no corresponde al estado actual.',
  VALIDACION: 'Hay datos con errores. Revisa los campos marcados.',
  LIMITE_EXCEDIDO: 'Demasiadas solicitudes. Espera un momento e intenta de nuevo.',
  VERSION_OBSOLETA: 'Otra persona modificó este registro. Recarga para ver la versión actual.',
  PRESUPUESTO_EXCEDIDO: 'El costo proyectado supera el presupuesto autorizado.',
  ERROR_INTERNO: 'Error interno del servidor.',
  PRECONDICION_FALLIDA: 'Otra persona modificó este registro. Recarga para ver la versión actual.',
  RED: 'No se pudo conectar con el servidor. Revisa tu conexión.',
  SESION_EXPIRADA: 'Tu sesión expiró. Vuelve a ingresar.',
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: CodigoCliente | string;
  readonly title: string;
  readonly detail: string | undefined;
  readonly errors: ErrorCampo[];
  readonly type: string;
  readonly requestId: string | undefined;
  readonly problem: ProblemDetail | undefined;

  constructor(init: { status: number; code: string; title?: string; detail?: string; errors?: unknown[]; type?: string; requestId?: string; problem?: ProblemDetail }) {
    super(init.detail || init.title || MENSAJES_ERROR[init.code] || init.code);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.title = init.title ?? MENSAJES_ERROR[init.code] ?? 'Error';
    this.detail = init.detail;
    this.errors = (init.errors ?? []).map((e) => (typeof e === 'object' && e !== null ? (e as ErrorCampo) : { mensaje: String(e) }));
    this.type = init.type ?? 'about:blank';
    this.requestId = init.requestId;
    this.problem = init.problem;
  }

  /** Mensaje para mostrar al usuario (español). */
  get mensaje(): string {
    const base = MENSAJES_ERROR[this.code];
    if (base && this.detail && this.detail !== base) return `${base} ${this.detail}`;
    return base ?? this.detail ?? this.title;
  }

  /** Errores por campo (VALIDACION 422) listos para setError de React Hook Form. */
  erroresPorCampo(): Record<string, string> {
    const r: Record<string, string> = {};
    for (const e of this.errors) {
      const campo = e.campo ?? (Array.isArray(e.path) ? e.path.join('.') : e.path);
      const msg = e.mensaje ?? e.message;
      if (campo && msg && !r[campo]) r[campo] = msg;
    }
    return r;
  }
}

function codigoPorStatus(status: number): string {
  if (status === 412) return 'PRECONDICION_FALLIDA';
  if (status === 401) return 'AUTH_TOKEN_INVALIDO';
  if (status === 403) return 'PERMISO_DENEGADO';
  if (status === 404) return 'NO_ENCONTRADO';
  if (status === 422) return 'VALIDACION';
  if (status === 429) return 'LIMITE_EXCEDIDO';
  return `HTTP_${status}`;
}

/** Convierte cualquier respuesta no-2xx en ApiError (RFC 7807 si el cuerpo lo es; si no, por status). */
export async function errorDesdeRespuesta(res: Response): Promise<ApiError> {
  const requestId = res.headers.get('x-request-id') ?? undefined;
  const tipo = res.headers.get('content-type') ?? '';
  let problem: ProblemDetail | undefined;
  if (tipo.includes('json')) {
    try {
      problem = (await res.json()) as ProblemDetail;
    } catch {
      problem = undefined;
    }
  }
  if (problem && typeof problem === 'object') {
    // Nest a veces entrega `message` en vez de `detail`; se normaliza.
    const detalleNest = Array.isArray(problem.message) ? undefined : (problem.message as string | undefined);
    const erroresNest = Array.isArray(problem.message) ? (problem.message as unknown[]) : undefined;
    const init: ConstructorParameters<typeof ApiError>[0] = {
      status: problem.status ?? res.status,
      code: problem.code ?? codigoPorStatus(res.status),
      problem,
    };
    if (problem.title) init.title = problem.title;
    const detail = problem.detail ?? detalleNest;
    if (detail) init.detail = detail;
    const errors = problem.errors ?? erroresNest;
    if (errors) init.errors = errors;
    if (problem.type) init.type = problem.type;
    if (requestId) init.requestId = requestId;
    return new ApiError(init);
  }
  const init: ConstructorParameters<typeof ApiError>[0] = { status: res.status, code: codigoPorStatus(res.status), title: res.statusText || `Error ${res.status}` };
  if (requestId) init.requestId = requestId;
  return new ApiError(init);
}

export type Query = Record<string, string | number | boolean | null | undefined>;

export interface OpcionesSolicitud {
  query?: Query | undefined;
  body?: unknown;
  headers?: Record<string, string>;
  /** ETag de la versión leída (concurrencia optimista). */
  ifMatch?: string | null | undefined;
  /** Agrega Idempotency-Key (POST que crean recursos). */
  idempotente?: boolean;
  respuesta?: 'json' | 'blob' | 'text';
  signal?: AbortSignal;
  /** No intentar refresh ante 401 (rutas de autenticación). */
  sinReintento?: boolean;
}

export interface Respuesta<T> {
  data: T;
  status: number;
  etag: string | null;
  headers: Headers;
}

export interface TokensAcceso {
  access_token: string;
  expires_in?: number;
  token_type?: string;
}

export interface ConfigCliente {
  baseUrl: string;
  fetch?: typeof fetch;
  /** Se invoca cuando el refresh falla: la sesión terminó. */
  onSesionExpirada?: () => void;
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function construirUrl(base: string, path: string, query?: Query): string {
  const b = base.endsWith('/') ? base.slice(0, -1) : base;
  const p = path.startsWith('/') ? path : `/${path}`;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  const s = qs.toString();
  return `${b}${p}${s ? `?${s}` : ''}`;
}

export class HttpClient {
  private accessToken: string | null = null;
  private tenantId: string | null = null;
  private refrescando: Promise<boolean> | null = null;
  private readonly cfg: ConfigCliente;

  constructor(cfg: ConfigCliente) {
    this.cfg = cfg;
  }

  private get fetchFn(): typeof fetch {
    return this.cfg.fetch ?? ((...a) => fetch(...a));
  }

  setSesionExpirada(cb: () => void) {
    this.cfg.onSesionExpirada = cb;
  }
  setAccessToken(t: string | null) {
    this.accessToken = t;
  }
  getAccessToken(): string | null {
    return this.accessToken;
  }
  setTenant(id: string | null) {
    this.tenantId = id;
  }
  getTenant(): string | null {
    return this.tenantId;
  }

  /**
   * Rota el refresh (cookie HttpOnly). Una sola solicitud en vuelo aunque varias peticiones reciban 401 a la vez.
   * Devuelve true si se obtuvo un nuevo token de acceso.
   */
  refrescar(): Promise<boolean> {
    if (!this.refrescando) {
      this.refrescando = (async () => {
        try {
          const headers: Record<string, string> = { Accept: 'application/json' };
          if (this.tenantId) headers['X-Tenant-Id'] = this.tenantId;
          const res = await this.fetchFn(construirUrl(this.cfg.baseUrl, '/auth/refresh'), { method: 'POST', credentials: 'include', headers });
          if (!res.ok) {
            this.accessToken = null;
            return false;
          }
          const tokens = (await res.json()) as TokensAcceso;
          if (!tokens?.access_token) return false;
          this.accessToken = tokens.access_token;
          return true;
        } catch {
          return false;
        } finally {
          // Quienes ya tienen la promesa comparten el resultado; los 401 que lleguen después comparan el token (ver solicitar).
          this.refrescando = null;
        }
      })();
    }
    return this.refrescando;
  }

  async solicitar<T>(metodo: string, path: string, opciones: OpcionesSolicitud = {}): Promise<Respuesta<T>> {
    const ejecutar = async (): Promise<Response> => {
      const headers: Record<string, string> = { Accept: 'application/json, application/problem+json', ...opciones.headers };
      let body: BodyInit | undefined;
      if (opciones.body instanceof FormData || opciones.body instanceof Blob) body = opciones.body;
      else if (opciones.body !== undefined) {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(opciones.body);
      }
      if (this.accessToken) headers.Authorization = `Bearer ${this.accessToken}`;
      if (this.tenantId) headers['X-Tenant-Id'] = this.tenantId;
      if (opciones.ifMatch) headers['If-Match'] = opciones.ifMatch;
      if (opciones.idempotente) headers['Idempotency-Key'] = uuid();
      const init: RequestInit = { method: metodo, headers, credentials: 'include' };
      if (body !== undefined) init.body = body;
      if (opciones.signal) init.signal = opciones.signal;
      try {
        return await this.fetchFn(construirUrl(this.cfg.baseUrl, path, opciones.query), init);
      } catch (e) {
        if ((e as Error)?.name === 'AbortError') throw e;
        throw new ApiError({ status: 0, code: 'RED' });
      }
    };

    const tokenUsado = this.accessToken;
    let res = await ejecutar();
    let reintentado = false;
    const esAuth = path.startsWith('/auth/');
    if (res.status === 401 && !opciones.sinReintento && !esAuth) {
      // Si otra petición ya rotó el token mientras esta viajaba, basta con reintentar.
      const ok = (this.accessToken !== null && this.accessToken !== tokenUsado) || (await this.refrescar());
      if (ok) {
        reintentado = true;
        res = await ejecutar();
      } else {
        this.cfg.onSesionExpirada?.();
        throw new ApiError({ status: 401, code: 'SESION_EXPIRADA' });
      }
    }
    if (!res.ok) {
      const err = await errorDesdeRespuesta(res);
      // Un 401 que persiste tras un refresh exitoso es un error de negocio de la operación (p. ej. contraseña actual incorrecta), no una sesión vencida.
      if (res.status === 401 && !esAuth && !reintentado) this.cfg.onSesionExpirada?.();
      throw err;
    }
    const etag = res.headers.get('etag');
    let data: unknown = undefined;
    if (res.status !== 204) {
      if (opciones.respuesta === 'blob') data = await res.blob();
      else if (opciones.respuesta === 'text') data = await res.text();
      else {
        const txt = await res.text();
        data = txt ? JSON.parse(txt) : undefined;
      }
    }
    return { data: data as T, status: res.status, etag, headers: res.headers };
  }

  async get<T>(path: string, query?: Query, opciones: OpcionesSolicitud = {}): Promise<T> {
    return (await this.solicitar<T>('GET', path, { ...opciones, query })).data;
  }
  async post<T>(path: string, body?: unknown, opciones: OpcionesSolicitud = {}): Promise<T> {
    return (await this.solicitar<T>('POST', path, { ...opciones, body })).data;
  }
  async patch<T>(path: string, body: unknown, opciones: OpcionesSolicitud = {}): Promise<T> {
    return (await this.solicitar<T>('PATCH', path, { ...opciones, body })).data;
  }
  async put<T>(path: string, body: unknown, opciones: OpcionesSolicitud = {}): Promise<T> {
    return (await this.solicitar<T>('PUT', path, { ...opciones, body })).data;
  }
  async delete<T>(path: string, opciones: OpcionesSolicitud = {}): Promise<T> {
    return (await this.solicitar<T>('DELETE', path, opciones)).data;
  }

  /** Descarga un archivo (xlsx, pdf, csv, json) y lo entrega al navegador sin scripts en línea. */
  async descargar(path: string, query: Query | undefined, nombrePorDefecto: string): Promise<void> {
    const r = await this.solicitar<Blob>('GET', path, { query, respuesta: 'blob', headers: { Accept: '*/*' } });
    const disp = r.headers.get('content-disposition') ?? '';
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disp);
    guardarArchivo(r.data, m?.[1] ? decodeURIComponent(m[1]) : nombrePorDefecto);
  }
}

export function guardarArchivo(blob: Blob, nombre: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Decodifica el payload de un JWT sin validar la firma (solo para la interfaz; el servidor valida). */
export function decodificarJwt<T = Record<string, unknown>>(token: string): T | null {
  try {
    const parte = token.split('.')[1];
    if (!parte) return null;
    const b64 = parte.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(parte.length / 4) * 4, '=');
    const bin = atob(b64);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as T;
  } catch {
    return null;
  }
}
